import {
  paginatedResultScenarioItemSchema,
  scenarioItemAncestorSchema,
  type scenarioItemBulkEditRequestSchema,
  scenarioItemBulkUpdateResultSchema,
  scenarioItemSchema,
  type skippedBulkDependencySchema,
} from '@tempo-io/adaptive-planner-dto';
import { z } from 'zod';
import { tempoApi } from '@/common/api/tempoApi';
import type { ItemAncestor } from '@/common/api/types/items';
import type { ScenarioItemPatchRequest } from '@/common/api/types/planner/timelineItem';

type PaginatedScenarioItems = z.infer<typeof paginatedResultScenarioItemSchema>;

const getScenarioItems = async ({
  loopId,
  scenarioId,
  filter,
  limit,
  cursor,
}: {
  loopId: number;
  scenarioId: number;
  filter?: string;
  limit?: number;
  cursor?: string;
}) => {
  const searchParams: Record<string, string> = {};
  if (filter !== undefined) {
    searchParams.filter = filter;
  }
  if (limit !== undefined) {
    searchParams.limit = String(limit);
  }
  if (cursor !== undefined) {
    searchParams.cursor = cursor;
  }

  return tempoApi()
    .get(`api/v1/adaptive-planner/loops/${loopId}/scenarios/${scenarioId}/scenario-items`, {
      searchParams,
    })
    .json()
    .then((response) => paginatedResultScenarioItemSchema.parse(response));
};

const getScenarioItem = async ({
  loopId,
  scenarioId,
  itemId,
}: {
  loopId: number;
  scenarioId: number;
  itemId: number;
}) => {
  const response = await tempoApi()
    .get(`api/v1/adaptive-planner/loops/${loopId}/scenarios/${scenarioId}/scenario-items/${itemId}`)
    .json();
  return scenarioItemSchema.parse(response);
};

const getScenarioItemAncestors = async ({
  loopId,
  scenarioId,
  itemId,
}: {
  loopId: number;
  scenarioId: number;
  itemId: number;
}): Promise<ItemAncestor[]> => {
  const response = await tempoApi()
    .get(
      `api/v1/adaptive-planner/loops/${loopId}/scenarios/${scenarioId}/scenario-items/${itemId}/ancestors`,
    )
    .json();
  return z.array(scenarioItemAncestorSchema).parse(response);
};

const updateScenarioItem = async ({
  loopId,
  scenarioId,
  itemId,
  data,
}: {
  loopId: number;
  scenarioId: number;
  itemId: number;
  data: ScenarioItemPatchRequest;
}) => {
  const response = await tempoApi()
    .patch(
      `api/v1/adaptive-planner/loops/${loopId}/scenarios/${scenarioId}/scenario-items/${itemId}`,
      { json: data },
    )
    .json();
  return scenarioItemSchema.parse(response);
};

type BulkDependencyDirection =
  | { predecessorIds: number[]; successorIds?: never; waitDays?: number }
  | { successorIds: number[]; predecessorIds?: never; waitDays?: number };

type ScenarioItemBulkEditRequest = Omit<
  z.infer<typeof scenarioItemBulkEditRequestSchema>,
  'dependency'
> & {
  dependency?: BulkDependencyDirection;
};

type SkippedBulkDependency = z.infer<typeof skippedBulkDependencySchema>;

type SkippedBulkDependencyReason = SkippedBulkDependency['reason'];

type ScenarioItemBulkUpdateResult = z.infer<typeof scenarioItemBulkUpdateResultSchema>;

type BulkDependencyResult = NonNullable<ScenarioItemBulkUpdateResult['dependencyResult']>;

const stageBulkEdit = async ({
  loopId,
  scenarioId,
  sessionId,
  request,
}: {
  loopId: number;
  scenarioId: number;
  sessionId: string;
  request: ScenarioItemBulkEditRequest;
}): Promise<void> => {
  const payload = {
    sessionId,
    request: {} as Record<string, unknown>,
  };

  if (request.teamId !== undefined) {
    payload.request.teamId = request.teamId;
  }
  if (request.estimationId !== undefined) {
    payload.request.estimationId = request.estimationId;
  }
  if (request.earliestStartDateTime !== undefined) {
    payload.request.earliestStartDateTime = request.earliestStartDateTime;
  }
  if (request.deadlineDateTime !== undefined) {
    payload.request.deadlineDateTime = request.deadlineDateTime;
  }
  if (request.itemStatusId != null) {
    payload.request.itemStatusId = request.itemStatusId;
  }
  if (request.dependency !== undefined) {
    payload.request.dependency = request.dependency;
  }
  payload.request.itemIds = request.itemIds;

  await tempoApi().post(
    `api/v1/adaptive-planner/loops/${loopId}/scenarios/${scenarioId}/scenario-items/stage`,
    { json: payload },
  );
};

const commitBulkEdit = async ({
  loopId,
  scenarioId,
  sessionId,
}: {
  loopId: number;
  scenarioId: number;
  sessionId: string;
}): Promise<ScenarioItemBulkUpdateResult> => {
  const response = await tempoApi()
    .post(`api/v1/adaptive-planner/loops/${loopId}/scenarios/${scenarioId}/scenario-items/commit`, {
      json: { sessionId },
    })
    .json();
  return scenarioItemBulkUpdateResultSchema.parse(response);
};

export const LoopPlannerScenarioItemsApi = {
  getScenarioItems,
  getScenarioItem,
  getScenarioItemAncestors,
  updateScenarioItem,
  stageBulkEdit,
  commitBulkEdit,
} as const;

export type {
  BulkDependencyDirection,
  BulkDependencyResult,
  PaginatedScenarioItems,
  ScenarioItemBulkEditRequest,
  ScenarioItemBulkUpdateResult,
  SkippedBulkDependency,
  SkippedBulkDependencyReason,
};
